import React, { useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import DetailsBox from '../../../components/Cards/DetailsBox'
import API from '../../../services/API'
import {
  Alert,
  AlertTitle,
  Avatar,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { GlobalContext } from '../../../App'
import useIsMobile from '../../../helpers/useIsMobile'

const AlreadyFilledReview = ({ shift, applications, onClose, loadApplications }) => {
  const [sendEmail, setSendEmail] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [reviewError, setReviewError] = useState(null)
  const { setToast } = useContext(GlobalContext)
  const isMobile = useIsMobile('sm')

  const clearApplications = () => {
    setSubmitting(true)
    setReviewError(null)

    API.post(
      '/shift-application/reject-filled',
      { shiftID: shift._id, sendEmail },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (res.status === 200) {
          const clearedCount = res.data?.rejectedCount ?? applications.length
          setToast({
            message: `Cleared ${clearedCount} pending application${
              clearedCount === 1 ? '' : 's'
            }`,
            severity: 'success',
          })
          loadApplications()
          onClose()
        } else {
          setReviewError(
            res.data?.error ||
              res.data?.message ||
              'Unable to clear these applications. Please try again later.',
          )
        }
      })
      .catch((error) => {
        setReviewError(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Unable to clear these applications. Please try again later.',
        )
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Stack
      sx={{ width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', boxSizing: 'border-box', p: 1 }}
      gap={2}
    >
      <DetailsBox
        title={`Applicants for ${shift?.clinicalRole ?? 'this shift'}`}
        buttonRight={
          isMobile && (
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )
        }
        rows={[
          {
            label: shift?.event ? 'Event' : 'Type',
            value: shift?.event ? (
              <Link to={`/events/${shift.event._id}`} target="_blank">
                {shift.event.title}
              </Link>
            ) : (
              shift?.shiftType
            ),
            fullWidth: true,
          },
          {
            label: 'Start',
            value: moment(shift?.start).format('Do MMM - HH:mm'),
          },
          {
            label: 'End',
            value: moment(shift?.end).format('Do MMM - HH:mm'),
          },
        ]}
      >
        <Stack gap={1.5}>
          <Alert severity="warning">
            This shift has already been filled — most likely assigned
            directly from the event page rather than through a shift
            application. The {applications.length} pending applicant
            {applications.length === 1 ? '' : 's'} below can&apos;t be
            allocated to it.
          </Alert>

          <Stack gap={1}>
            {applications.map((application) => {
              const userAccount = application.employee?.userAccount ?? {}
              return (
                <Stack
                  key={application._id}
                  direction="row"
                  gap={1}
                  alignItems="center"
                >
                  <Avatar
                    src={userAccount.photograph}
                    sx={{ width: 28, height: 28 }}
                  />
                  <Typography variant="body2">
                    {userAccount.firstName} {userAccount.lastName}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Notify applicants by email that the shift was filled"
          />

          {reviewError && <Alert severity="error">{reviewError}</Alert>}

          <Button
            variant="contained"
            color="warning"
            disabled={submitting}
            onClick={clearApplications}
          >
            Clear {applications.length} Pending Application
            {applications.length === 1 ? '' : 's'}
          </Button>
        </Stack>
      </DetailsBox>
    </Stack>
  )
}

export default AlreadyFilledReview
